import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Subject, fromEvent, Observable, from, of } from 'rxjs';
import { filter, debounceTime, map, tap } from 'rxjs/operators';

import { LazyLoadConfig } from './lazy-load-config';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CorePlatformService } from '@core/data-providers/platform/platform.service';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { isDefined } from '@frontend/common/utils';

export function hasIntersectionObserver(): boolean {
    const hasIntersection = 'IntersectionObserver' in window;
    const userAgent = window.navigator.userAgent;
    const matches = /edge\/(\d*)\./i.exec(userAgent);
    const isEdge = !!matches && matches.length > 1;
    const isEdgeVersion16OrBetter = isEdge && (!!matches && parseInt(matches[1]) > 15);

    return hasIntersection && (!isEdge || isEdgeVersion16OrBetter);
}

@Injectable({
    providedIn: 'root',
})
export class LazyLoadObserverService {
    private readonly intersectionObserver: IntersectionObserver | undefined;

    private readonly eventObservable$: Observable<HTMLElement>;

    constructor(
        config: LazyLoadConfig,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platform: CorePlatformService
    ) {
        if (platform.isBrowser() && hasIntersectionObserver()) {
            const loadingSubject$: Subject<HTMLElement> = new Subject();
            this.eventObservable$ = from(loadingSubject$);

            this.intersectionObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
                entries.forEach((entry: IntersectionObserverEntry) => {
                    if (!entry.isIntersecting) { return false; }
                    loadingSubject$.next(entry.target as HTMLElement);
                    return true;
                }, {
                    threshold: config.threshold,
                    rootMargin: config.rootMargin,
                    root: config.root,
                });
            });
        } else {
            this.eventObservable$ = fromEvent(this.window, 'scroll').pipe(
                debounceTime(50),
                map((ev) => ev.target as HTMLElement)
            );
        }
    }

    public observeElement(htmlElement: HTMLElement): Observable<HTMLElement> {
        if (isDefined(this.intersectionObserver)) {
            this.intersectionObserver.observe(htmlElement);
            return this.eventObservable$.pipe(
                filter((observedHTMLElement: HTMLElement) => observedHTMLElement === htmlElement),
                tap(() => { this.unobserveElement(htmlElement); }),
            );
        }
        if (this.isVisible(htmlElement)) {
            return of(htmlElement);
        }
        return this.eventObservable$.pipe(
            filter(() => this.isVisible(htmlElement))
        );
    }

    public unobserveElement(htmlElement: HTMLElement) {
        if (isDefined(this.intersectionObserver)) {
            this.intersectionObserver.unobserve(htmlElement);
        }
    }

    public static waitForElmement(elementId): Promise<Element> {
        return new Promise(resolve => {
            if (document.getElementById(elementId)) {
                resolve(document.getElementById(elementId)); return;
            }
            const observer = new MutationObserver(_mutations => {
                if (document.getElementById(elementId)) {
                    resolve(document.getElementById(elementId));
                    observer.disconnect();
                }
            });
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        });
    }

    private isVisible(htmlElement: HTMLElement): boolean {
        const scrollPosition = this.getScrollPosition();
        const htmlElementOffset = htmlElement.offsetTop;
        return htmlElementOffset <= scrollPosition;
    }

    @BrowserOnly<LazyLoadObserverService, number>(0)
    private getScrollPosition(): number {
    // Getting screen size and scroll position for IE
        // eslint-disable-next-line deprecation/deprecation
        return (this.window.scrollY || this.window.pageYOffset)
      + (document.documentElement.clientHeight || document.body.clientHeight);
    }
}
